import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { BellRing, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Checkbox } from "../../../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Switch } from "../../../ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Textarea } from "../../../ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../ui/tooltip";
import { RULE_TIER_COLORS } from "./PolicyComplianceCharts";
import type { AlertRuleProposalInput } from "../lib/api/alert-rules";
import { ruleDefinitions, type RuleId, type RuleTier } from "../lib/api/policy-compliance";
import type { AlertRule, AlertRuleProposal, AlertRuleScope } from "../lib/api/types";

/* ── shared bits ──────────────────────────────────────────────────────────── */

const AGREEMENT_SEGMENTS = [
  { value: "small_ticket", label: "Small-ticket" },
  { value: "rest", label: "Rest of book" },
  { value: "pg", label: "PG" },
  { value: "corp", label: "Corp" },
  { value: "all", label: "All" },
] as const;

type AgreementSegment = (typeof AGREEMENT_SEGMENTS)[number]["value"];

function segmentLabel(segment: AgreementSegment) {
  return AGREEMENT_SEGMENTS.find((s) => s.value === segment)?.label ?? segment;
}

function ruleTierColor(tier: RuleTier) {
  return tier === "DQ" ? "var(--neutral-inactive)" : RULE_TIER_COLORS[tier];
}

function RuleBadge({ ruleId }: { ruleId: RuleId }) {
  const def = ruleDefinitions.find((d) => d.ruleId === ruleId);
  const color = ruleTierColor(def?.ruleTier ?? "A");
  return (
    <Badge
      variant="outline"
      className="px-2 py-0.5 text-[11px]"
      style={{
        borderColor: `color-mix(in oklch, ${color} 55%, transparent)`,
        background: `color-mix(in oklch, ${color} 16%, transparent)`,
        color,
      }}
    >
      {ruleId}
    </Badge>
  );
}

export function ScopeCell({ scope }: { scope: AlertRuleScope }) {
  if (scope.type === "agreement_rate") {
    return (
      <span className="text-sm text-foreground">
        {segmentLabel(scope.segment)} agreement rate
      </span>
    );
  }
  const def = ruleDefinitions.find((d) => d.ruleId === scope.ruleId);
  return (
    <span className="inline-flex items-center gap-2">
      <RuleBadge ruleId={scope.ruleId} />
      <span className="text-sm text-foreground">{def?.label ?? scope.ruleId}</span>
    </span>
  );
}

export function thresholdText(scope: AlertRuleScope) {
  return scope.type === "policy_rule"
    ? `${scope.thresholdDaysUnreconciled} days unreconciled`
    : `< ${scope.thresholdPct}%`;
}

export function officeScopeText(officeScope: "all" | string[]) {
  return officeScope === "all" ? "All Offices" : officeScope.join(", ");
}

function TruncatedList({ items, max = 2 }: { items: string[]; max?: number }) {
  if (items.length === 0) return <span className="text-neutral-inactive">—</span>;
  if (items.length <= max) return <span className="text-sm">{items.join(", ")}</span>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default text-sm underline decoration-dotted decoration-border underline-offset-4">
            {items.slice(0, max).join(", ")} +{items.length - max} more
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <span className="text-xs">{items.join(", ")}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TypeBadge({ scope }: { scope: AlertRuleScope }) {
  return (
    <Badge variant="outline" className="border-border text-xs font-medium">
      {scope.type === "policy_rule" ? "Policy Rule" : "Agreement Rate"}
    </Badge>
  );
}

/* ── Section 1: active alert rules table ─────────────────────────────────── */

const columnHelper = createColumnHelper<AlertRule>();

export function ActiveAlertRulesTable({
  rules,
  onProposeChange,
}: {
  rules: AlertRule[];
  onProposeChange: (rule: AlertRule) => void;
}) {
  const columns = useMemo<ColumnDef<AlertRule, any>[]>(
    () => [
      columnHelper.display({
        id: "type",
        header: "Type",
        cell: (info) => <TypeBadge scope={info.row.original.scope} />,
      }),
      columnHelper.display({
        id: "scope",
        header: "Scope",
        cell: (info) => <ScopeCell scope={info.row.original.scope} />,
      }),
      columnHelper.display({
        id: "threshold",
        header: "Threshold",
        cell: (info) => (
          <span className="text-sm tabular-nums">{thresholdText(info.row.original.scope)}</span>
        ),
      }),
      columnHelper.display({
        id: "officeScope",
        header: "Office Scope",
        cell: (info) => {
          const { officeScope } = info.row.original;
          if (officeScope === "all") return <span className="text-sm">All Offices</span>;
          return <TruncatedList items={officeScope} />;
        },
      }),
      columnHelper.display({
        id: "recipients",
        header: "Recipients",
        cell: (info) => <TruncatedList items={info.row.original.notificationRecipients} max={1} />,
      }),
      columnHelper.display({
        id: "active",
        header: "Active",
        cell: (info) =>
          info.row.original.active ? (
            <Badge
              variant="outline"
              className="border-primary/50 bg-amber-dim px-2 py-0.5 text-[11px] text-primary"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-neutral-inactive/40 bg-neutral-inactive-dim px-2 py-0.5 text-[11px] text-neutral-inactive"
            >
              Inactive
            </Badge>
          ),
      }),
      columnHelper.display({
        id: "lastTriggered",
        header: "Last Triggered",
        cell: (info) => {
          const value = info.row.original.lastTriggeredAt;
          if (!value)
            return <span className="text-sm text-neutral-inactive">Never triggered</span>;
          return (
            <span className="text-sm text-muted-foreground">
              {formatDistanceToNowStrict(parseISO(value), { addSuffix: true })}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onProposeChange(info.row.original)}
          >
            Propose change
          </Button>
        ),
      }),
    ],
    [onProposeChange],
  );

  const table = useReactTable({
    data: rules,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="whitespace-nowrap text-xs uppercase tracking-wider"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ── proposal dialog ─────────────────────────────────────────────────────── */

export interface DialogTarget {
  rule: AlertRule | null;
}

export function AlertRuleDialog({
  open,
  onOpenChange,
  target,
  offices,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DialogTarget | null;
  offices: { officeName: string; liveOnAthena: boolean }[];
  onSubmit: (input: AlertRuleProposalInput) => void;
}) {
  const rule = target?.rule ?? null;
  const isEdit = Boolean(rule);
  const existing = rule?.scope;

  const [alertType, setAlertType] = useState<AlertRuleScope["type"]>(
    existing?.type ?? "policy_rule",
  );
  const [ruleId, setRuleId] = useState<RuleId | "">(
    existing?.type === "policy_rule" ? existing.ruleId : "",
  );
  const [days, setDays] = useState(
    existing?.type === "policy_rule" ? String(existing.thresholdDaysUnreconciled) : "",
  );
  const [segment, setSegment] = useState<AgreementSegment | "">(
    existing?.type === "agreement_rate" ? existing.segment : "",
  );
  const [pct, setPct] = useState(
    existing?.type === "agreement_rate" ? String(existing.thresholdPct) : "",
  );
  const [officeMode, setOfficeMode] = useState<"all" | "specific">(
    rule && rule.officeScope !== "all" ? "specific" : "all",
  );
  const [selectedOffices, setSelectedOffices] = useState<string[]>(
    rule && rule.officeScope !== "all" ? rule.officeScope : [],
  );
  // Committed list plus the in-progress entry, rather than one comma-separated string: a typo in
  // the separator silently produced one malformed address, and there was no way to see or remove
  // an individual recipient. Same shape the Risk Pricing Engine dialog uses.
  const [recipients, setRecipients] = useState<string[]>(rule?.notificationRecipients ?? []);
  const [recipientInput, setRecipientInput] = useState("");
  const [active, setActive] = useState(rule?.active ?? true);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  function addRecipient() {
    const email = recipientInput.trim();
    if (!email) return;
    setRecipients((previous) => (previous.includes(email) ? previous : [...previous, email]));
    setRecipientInput("");
  }

  function removeRecipient(email: string) {
    setRecipients((previous) => previous.filter((item) => item !== email));
  }

  function submit() {
    // Anything still sitting in the input counts — losing a typed address because the user pressed
    // Save instead of "+" would be the obvious trap in this design.
    const pending = recipientInput.trim();
    const parsedRecipients =
      pending && !recipients.includes(pending) ? [...recipients, pending] : recipients;

    if (alertType === "policy_rule" && (!ruleId || !days || Number(days) <= 0)) {
      setError("Pick a policy rule and a positive number of days.");
      return;
    }
    if (alertType === "agreement_rate" && (!segment || !pct || Number(pct) <= 0)) {
      setError("Pick a segment and a positive agreement-rate threshold.");
      return;
    }
    if (officeMode === "specific" && selectedOffices.length === 0) {
      setError("Select at least one office, or scope the rule to All Offices.");
      return;
    }
    if (parsedRecipients.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    if (!reason.trim()) {
      setError("A reason is required — reviewers approve the reason, not just the number.");
      return;
    }

    const scope: AlertRuleScope =
      alertType === "policy_rule"
        ? {
            type: "policy_rule",
            ruleId: ruleId as RuleId,
            thresholdDaysUnreconciled: Number(days),
          }
        : {
            type: "agreement_rate",
            segment: segment as AgreementSegment,
            thresholdPct: Number(pct),
          };

    setError(null);
    onSubmit({
      alertRuleId: rule?.alertRuleId ?? null,
      scope,
      officeScope: officeMode === "all" ? "all" : selectedOffices,
      notificationRecipients: parsedRecipients,
      active,
      reason: reason.trim(),
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Propose change" : "Propose new alert rule"}</DialogTitle>
          <DialogDescription>
            The change takes effect only once approved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label>Alert type</Label>
            <RadioGroup
              value={alertType}
              onValueChange={(v) => setAlertType(v as AlertRuleScope["type"])}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="policy_rule" id="type-policy" />
                <Label htmlFor="type-policy" className="font-normal">
                  Policy Rule
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="agreement_rate" id="type-agreement" />
                <Label htmlFor="type-agreement" className="font-normal">
                  Agreement Rate
                </Label>
              </div>
            </RadioGroup>
          </div>

          {alertType === "policy_rule" ? (
            <>
              <div className="space-y-2">
                <Label>Policy rule</Label>
                <Select value={ruleId} onValueChange={(v) => setRuleId(v as RuleId)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a rule" />
                  </SelectTrigger>
                  <SelectContent>
                    {ruleDefinitions.map((def) => (
                      <SelectItem key={def.ruleId} value={def.ruleId}>
                        <span className="inline-flex items-center gap-2">
                          <RuleBadge ruleId={def.ruleId} />
                          <span>{def.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="days">Days unreconciled before triggering</Label>
                <Input
                  id="days"
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  placeholder="e.g. 5"
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Segment</Label>
                <Select value={segment} onValueChange={(v) => setSegment(v as AgreementSegment)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGREEMENT_SEGMENTS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pct">Trigger when agreement rate falls below (%)</Label>
                <Input
                  id="pct"
                  type="number"
                  min={1}
                  max={100}
                  value={pct}
                  onChange={(e) => setPct(e.target.value)}
                  placeholder="e.g. 75"
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>Office scope</Label>
            <RadioGroup
              value={officeMode}
              onValueChange={(v) => setOfficeMode(v as "all" | "specific")}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="office-all" />
                <Label htmlFor="office-all" className="font-normal">
                  All Offices
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="specific" id="office-specific" />
                <Label htmlFor="office-specific" className="font-normal">
                  Specific offices
                </Label>
              </div>
            </RadioGroup>
            {officeMode === "specific" ? (
              <div className="mt-2 space-y-2 rounded-md border border-border p-3">
                {offices.map((office) => {
                  const checked = selectedOffices.includes(office.officeName);
                  return (
                    <div key={office.officeName} className="flex items-center gap-2">
                      <Checkbox
                        id={`office-${office.officeName}`}
                        checked={checked}
                        onCheckedChange={(next) =>
                          setSelectedOffices((prev) =>
                            next
                              ? [...prev, office.officeName]
                              : prev.filter((n) => n !== office.officeName),
                          )
                        }
                      />
                      <Label
                        htmlFor={`office-${office.officeName}`}
                        className={
                          office.liveOnAthena
                            ? "font-normal"
                            : "font-normal text-neutral-inactive"
                        }
                      >
                        {office.officeName}
                      </Label>
                      {office.liveOnAthena ? null : (
                        <span className="bg-neutral-inactive-dim px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-inactive">
                          Not yet live
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipients">Recipients</Label>
            <div className="flex gap-2">
              <Input
                id="recipients"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // Otherwise Enter submits the dialog with the address still uncommitted.
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="name@athena.example"
                maxLength={200}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 shrink-0 px-3"
                onClick={addRecipient}
                aria-label="Add recipient"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {recipients.length ? (
              <div className="flex flex-wrap gap-1.5">
                {recipients.map((email) => (
                  <span
                    key={email}
                    className="flex items-center gap-1.5 border border-border bg-secondary/40 px-2 py-1 text-xs"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      aria-label={`Remove ${email}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Add at least one recipient — placeholder pending the real notification directory.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
            <Label htmlFor="active" className="font-normal">
              Active
            </Label>
            <Switch id="active" checked={active} onCheckedChange={setActive} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this alert, and why this threshold?"
              rows={3}
            />
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit}>Submit proposal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProposeNewButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" onClick={onClick} className="gap-2">
      <Plus className="size-4" />
      Propose new alert rule
    </Button>
  );
}

/* ── Section 2: approval queue ───────────────────────────────────────────── */

function ProposalCard({
  proposal,
  onReview,
}: {
  proposal: AlertRuleProposal;
  onReview: (decision: "APPROVED" | "REJECTED", notes: string) => void;
}) {
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  function act(decision: "APPROVED" | "REJECTED") {
    if (!notes.trim()) {
      setError("Review notes are required.");
      return;
    }
    setError(null);
    onReview(decision, notes.trim());
  }

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <TypeBadge scope={proposal.scope} />
          <Badge
            variant="outline"
            className="border-primary/50 bg-amber-dim text-xs font-semibold text-primary"
          >
            {proposal.alertRuleId ? `Modifies ${proposal.alertRuleId}` : "New rule"}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {proposal.proposalId} · proposed by {proposal.proposedByRole}
          </span>
        </div>
        <CardTitle className="pt-1 text-base font-semibold">
          <ScopeCell scope={proposal.scope} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Threshold</dt>
            <dd className="tabular-nums">{thresholdText(proposal.scope)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Office scope</dt>
            <dd>{officeScopeText(proposal.officeScope)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">Recipients</dt>
            <dd className="break-words">{proposal.notificationRecipients.join(", ")}</dd>
          </div>
        </dl>

        <p className="rounded-md border border-border bg-secondary/40 p-3 text-sm text-foreground">
          {proposal.reason}
        </p>

        <div className="space-y-2">
          <Label htmlFor={`notes-${proposal.proposalId}`}>Review notes</Label>
          <Textarea
            id={`notes-${proposal.proposalId}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Required — recorded with the decision."
          />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <div className="flex gap-2">
          <Button size="sm" onClick={() => act("APPROVED")}>
            Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => act("REJECTED")}>
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ApprovalQueue({
  proposals,
  onReview,
}: {
  proposals: AlertRuleProposal[];
  onReview: (proposalId: string, decision: "APPROVED" | "REJECTED", notes: string) => void;
}) {
  const pending = proposals.filter((p) => p.status === "PENDING");

  if (pending.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-border py-14 text-center">
        <BellRing className="size-6 text-neutral-inactive" />
        <p className="text-sm font-medium text-neutral-inactive">No proposals awaiting review</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pending.map((proposal) => (
        <ProposalCard
          key={proposal.proposalId}
          proposal={proposal}
          onReview={(decision, notes) => onReview(proposal.proposalId, decision, notes)}
        />
      ))}
    </div>
  );
}
